import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Label, Row } from 'reactstrap';
import { InputField } from 'src/components';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import { setFromErrors } from 'src/helpers';
import { useGetItmcTeamInfoQuery, useItmcTeamCreateMutation } from 'src/rtk/features/instituteInspection/institutueInspectionApi';
import { useGetOrganizationsQuery } from 'src/rtk/features/setup/organization/setupOrganizationApi';
import { useGetSupervisorsByInstituteQuery } from 'src/rtk/features/trainingRecord/trainingRecordlApi';
import * as Yup from 'yup';
import UpdateItmcModal from './UpdateItmcModal';

const initialValues = {
    itmc_team_id: '',
    committee_name: '',
    institute_id: '',
    department_id: '',
    chairman_id: '',
    members: [],
};

const ItmcTeamCreate = () => {
    const [itmcTeamCreate, { isSuccess, isError, error }] = useItmcTeamCreateMutation();
    const [btnClicked, setBtnClicked] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [rowInfo, setRowInfo] = useState<any>(null);
    const [selectedChairman, setSelectedChairman] = useState<any>('');
    const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
    const [selectedSecretaries, setSelectedSecretaries] = useState<any>('');
    const history = useHistory();

    const { data: instituteList } = useGetOrganizationsQuery();
    const { data: teamInfo } = useGetItmcTeamInfoQuery();

    const [selectedInstitute, setSelectedInstitute] = useState<any>('');
    const { data: supervisors } = useGetSupervisorsByInstituteQuery(selectedInstitute ? selectedInstitute.value : '');

    const allInstitute: SelectOption[] =
        instituteList?.data?.map((item) => ({
            value: item.id!,
            label: item.organization_name,
        })) || [];

    const allChairmanMember: SelectOption[] =
        supervisors?.data?.map((item) => ({
            value: item.user_id!,
            label: item.full_name!,
        })) || [];

    const err: CustomError = error as CustomError;

    const {
        handleSubmit,
        getFieldProps,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        resetForm,
        setFieldValue,
        setErrors,
        setValues,
    } = useFormik({
        initialValues,

        validationSchema: Yup.object({
            institute_id: Yup.string().required('Select Institute'),
            committee_name: Yup.string().required('Team Name is required'),
            chairman_id: Yup.string().required('Select Chairman'),
        }),

        onSubmit: (values: any) => {
            console.log(values);
            itmcTeamCreate(values);
        },
    });

    useEffect(() => {
        if (isSuccess) {
            resetForm();
            setSelectedInstitute('');
            setSelectedChairman('');
            setSelectedMembers([]);
            setSelectedSecretaries('');
            toast.success('Team Created Successfully!');
        }
        if (isError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err.data, setErrors);
        }
    }, [isSuccess, isError]);

    useEffect(() => {
        if (selectedChairman !== '') {
            setFieldValue('chairman_id', selectedChairman?.value);
        }
    }, [selectedChairman]);

    useEffect(() => {
        if (selectedInstitute !== '') {
            setFieldValue('institute_id', selectedInstitute?.value);
        }
    }, [selectedInstitute]);

    // Handle member selection (member_type: 'member')
    const handleMember = (selectedOptions: any) => {
        setSelectedMembers(selectedOptions || []);
        updateMembersArray(selectedOptions || [], selectedSecretaries);
    };

    // Handle secretary selection (member_type: 'secretary')
    const handleSecretary = (selectedOptions: any) => {
        setSelectedSecretaries(selectedOptions);
        updateMembersArray(selectedMembers, selectedOptions || '');
    };

    // Update the members array with both members and secretaries
    const updateMembersArray = (members: any[], secretaries: any) => {
        const membersArray: any[] = [];

        // Add members with member_type: 'member'
        members.forEach((member) => {
            membersArray.push({
                member_id: member.value,
                member_type: 'member'
            });
        });

        // Add secretaries with member_type: 'secretary'
        // secretaries.forEach((secretary) => {
        //     membersArray.push({
        //         member_id: secretary.value,
        //         member_type: 'secretary'
        //     });
        // });
        secretaries && membersArray.push({
            member_id: secretaries.value,
            member_type: 'secretary'
        });
        console.log(secretaries);

        // console.log('Updated members array:', membersArray);
        setFieldValue('members', membersArray);
    };

    // Get selected member IDs to filter from available options
    const getSelectedMemberIds = () => {
        const selectedIds = [];
        if (values.chairman_id) selectedIds.push(values.chairman_id);
        selectedMembers.forEach(member => selectedIds.push(member.value));
        // selectedSecretaries.forEach(secretary => selectedIds.push(secretary.value));
        return selectedIds;
    };

    // Filter available options excluding chairman and already selected members/secretaries
    const getAvailableOptions = () => {
        const selectedIds = getSelectedMemberIds();
        return allChairmanMember.filter((option: any) => !selectedIds.includes(option.value));
    };

    const getInstitute = (instituteId: string) => {
        const institute = allInstitute.find(inst => inst.value === instituteId)?.label;
        return institute;
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BetterBreadcrumb
                    title={'Create ITMC Team'}
                />
                <Card>
                    <CardBody>
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            onReset={(e) => {
                                resetForm();
                                setSelectedChairman('');
                                setSelectedMembers([]);
                                setSelectedSecretaries([]);
                                setSelectedInstitute('');
                            }}
                        >
                            <Row>
                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="committee_name" className="form-label required-field">Team Name</Label>
                                        <InputField
                                            type="text"
                                            name='committee_name'
                                            placeholder='Team Name'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.committee_name}
                                            invalid={{ errors, touched }}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="institute_id" className="form-label required-field">Institute</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            value={selectedInstitute}
                                            onChange={setSelectedInstitute}
                                            options={allInstitute}
                                            placeholder='Select Institute'
                                            name="institute_id"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="chairman_id" className="form-label required-field">Chairman</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            value={selectedChairman}
                                            onChange={setSelectedChairman}
                                            options={allChairmanMember}
                                            placeholder='Select Chairman'
                                            name="chairman_id"
                                        />
                                        {errors.chairman_id && touched.chairman_id ? (
                                            <div className="text-danger">{errors.chairman_id as string}</div>
                                        ) : null}
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="members">Members</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            isMulti={true}
                                            value={selectedMembers}
                                            onChange={handleMember}
                                            options={getAvailableOptions()}
                                            placeholder='Select Members'
                                            name="members"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="secretary" className="form-label">Members Secretary</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            // isMulti={true}
                                            value={selectedSecretaries}
                                            onChange={handleSecretary}
                                            options={getAvailableOptions()}
                                            placeholder='Select Members Secretary'
                                            name="secretary"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <FormGroup className="mb-3 text-center">
                                    <Button
                                        type="reset"
                                        className="btn btn-secondary me-2"
                                    >
                                        Reset
                                    </Button>

                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        onClick={(e) => {
                                            setBtnClicked('submit');
                                        }}
                                    >
                                        Submit
                                    </button>
                                </FormGroup>
                            </Row>
                        </Form>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <table className='table table-responsive table-bordered'>
                            <thead className='table-secondary'>
                                <tr>
                                    <th>Team Name</th>
                                    <th>Institute</th>
                                    <th>Chairman</th>
                                    <th>Members and Secretary</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamInfo?.data ?
                                    <tr>
                                        <td>{teamInfo?.data?.committee_name}</td>
                                        <td>{getInstitute(teamInfo?.data?.institute_id)}</td>
                                        <td>{teamInfo?.data?.chairman_name}</td>
                                        <td>{teamInfo?.data?.itmc_team_members?.map((member: any) => member.member_name).join(', ')}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={(e) => {
                                                    setShowModal(true);
                                                    setRowInfo(teamInfo?.data);
                                                }}
                                            >
                                                Update
                                            </button>
                                        </td>
                                    </tr> :
                                    null}
                            </tbody>
                        </table>
                        {showModal && (
                            <UpdateItmcModal
                                isOpen={showModal}
                                toggle={() => setShowModal(false)}
                                rowInfo={rowInfo}
                            />
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default ItmcTeamCreate;